import { useMutation } from '@apollo/react-hooks';
import Tippy from '@tippy.js/react';
import cn from 'classnames';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import * as Yup from 'yup';
import Button from '~components/button';
import { Select } from '~components/form';
import StatusButton from '~components/navigation-panel/status-button';
import Typography from '~components/typography';
import {
  BookingStatusOption,
  CategoryOption,
  RentalTaxesRateTypeOption,
  StudentPayToOptions,
  TenancyLengthOptions,
} from '~graphql/__generated__/globalTypes';
import {
  SP_MutationStudentExpireBooking,
  SP_MutationStudentExpireBookingVariables,
} from '~graphql/__generated__/SP_MutationStudentExpireBooking';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { MUTATION_STUDENT_EXPIRE_BOOKING } from '~graphql/navigation-panel';
import { Router, useTranslation } from '~i18n';
import { Edit, IconPayment, IconRight, ShareEmail, SharePhone } from '~svg-components';
import currencyformatter from '~utils/currency-format';
import { getBillingCycle, getPropertyImageUrl, notEmpty } from '~utils/js-helper';
import { useGetRejectOptiosn } from '~utils/use-booking';
import {
  BookingProcesingOrderStatus,
  BookingStatusCanceledAndPastAndExpired,
  BookingStatusCanceledAndPastAndResident,
  BookingStatusProcesingExcludeApplied,
  NominationBookingStatusProcesing,
  useGetBookingStepTitle,
} from '~utils/use-status';
import styles from './booking-progress-card.module.scss';
import BookingProgressCardCountdown from './booking-progress-card-count-down';

interface IBookingProgressCardProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
  onDelete?: () => void;
}

const BookingProgressCard: React.FC<IBookingProgressCardProps &
  React.HTMLAttributes<HTMLDivElement>> = props => {
  const { t } = useTranslation(['student_portal']);
  const { booking, onDelete } = props;
  const [showDelete, setShowDelete] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const {
    id,
    status,
    heldToTime,
    tenancyOption,
    roomAllocation,
    property,
    category,
    priceForBillingCycle,
    rentalTaxRateType,
    balanceInfo,
    tenancyLengthOption,
    nominationPlan,
  } = booking;

  const bookingStepTitle = useGetBookingStepTitle(status, t);

  const isRollingBooking = tenancyLengthOption === TenancyLengthOptions.ROLLING;

  const DelectBookingSchema = Yup.object().shape({
    delete_reason: Yup.mixed().required(),
  });
  const [studentExpireBooking] = useMutation<
    SP_MutationStudentExpireBooking,
    SP_MutationStudentExpireBookingVariables
  >(MUTATION_STUDENT_EXPIRE_BOOKING, {
    onCompleted: () => {
      setShowDelete(false);
      if (onDelete) onDelete();
    },
  });

  const handleCardClick = useCallback(() => {
    if (!status) return;

    if (BookingProcesingOrderStatus.includes(status)) {
      Router.push(`/booking-process?bookingId=${id}`);
    }
    if (BookingStatusCanceledAndPastAndResident.includes(status)) {
      Router.push(`/booking-details?bookingId=${id}`);
    }
    if (status === BookingStatusOption.EXPIRED && category === CategoryOption.DIRECT_LET) {
      if (Number(balanceInfo?.walletBalance) > 0) {
        return Router.push(`/my-payments?bookingId=${id}`);
      }
      Router.push(`/filter?propertyId=${property?.id}`);
    }
  }, [balanceInfo, category, id, property, status]);

  const bookingIsPastOrArchived = useMemo(() => {
    if (!status) return false;
    return BookingStatusCanceledAndPastAndExpired.includes(status);
  }, [status]);

  const bookingIsNominationProcess = useMemo(() => {
    if (!status) return false;
    return NominationBookingStatusProcesing.includes(status);
  }, [status]);

  const cardName = useMemo(() => {
    return [
      roomAllocation?.bedName,
      roomAllocation?.buildingName,
      `${property?.name} , ${property?.city?.name}`,
    ]
      .filter(notEmpty)
      .join(', ');
  }, [property, roomAllocation]);

  const getPriceBillingCycle = useMemo(() => {
    if (
      status &&
      priceForBillingCycle &&
      BookingStatusProcesingExcludeApplied.includes(status) &&
      nominationPlan?.studentPayTo !== StudentPayToOptions.PARTNER
    ) {
      if (rentalTaxRateType) {
        let tax = '';
        if (rentalTaxRateType === RentalTaxesRateTypeOption.INCLUDED) {
          tax = `(${t('select_bed.rax.incl')})`;
        } else {
          tax = `(${t('select_bed.rax.excl')})`;
        }
        return `${property?.currency} ${currencyformatter(
          priceForBillingCycle,
          property?.currency
        )}/${t(getBillingCycle(property?.billingCycle) || '')} ${tax}`;
      } else {
        return `${property?.currency} ${currencyformatter(
          priceForBillingCycle,
          property?.currency
        )}/${t(getBillingCycle(property?.billingCycle) || '')}`;
      }
    }
  }, [nominationPlan, priceForBillingCycle, property, rentalTaxRateType, status, t]);

  const getDeleteOptions = useGetRejectOptiosn(t);

  const onSubmit = useCallback(
    (value: any) => {
      if (value && booking.id) {
        studentExpireBooking({
          variables: {
            bookingId: booking?.id,
            reason: value.delete_reason.value,
          },
        });
      }
    },
    [booking, studentExpireBooking]
  );

  const bookingDelete = useMemo(() => {
    return (
      <div
        className="booking-delete"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {category === CategoryOption.NOMINATION && (
          <>
            <Typography variant="h9" component="div">
              {t('nomination.countDown.reject')}
            </Typography>
            <Typography variant="body3" component="div" style={{ marginBottom: 8 }}>
              {t('nomination.countDown.reject.sub_title')}
            </Typography>
          </>
        )}
        {category === CategoryOption.DIRECT_LET && (
          <Typography variant="h9">{t('please_choose_a_reason')}</Typography>
        )}
        <Formik
          initialValues={{
            delete_reason: '',
          }}
          onSubmit={value => {
            onSubmit(value);
          }}
          validateOnBlur
          validationSchema={DelectBookingSchema}
        >
          <Form>
            <Select selectType="custom" options={getDeleteOptions} name="delete_reason" />
            <div className="booking-delete__submit-btn">
              <Typography
                variant="body3"
                cursor="pointer"
                textDecoration="underline"
                className="booking-delete__submit-close"
                onClick={() => {
                  setShowDelete(false);
                }}
              >
                {t('form.close')}
              </Typography>
              <Button size="small" type="submit">
                {category === CategoryOption.NOMINATION ? t('yes_reject_offer') : t('yes_delete')}
              </Button>
            </div>
          </Form>
        </Formik>
        <style jsx>
          {`
            .booking-delete {
              width: 357px;
              padding: 16px;
              border-radius: 4px;
              background: var(--color-white);
              box-shadow: 0 2px 8px 0 rgba(76, 76, 76, 0.16);
              :global(&__submit-btn) {
                text-align: right;
                margin-top: 16px;
              }
              :global(&__submit-close) {
                margin-right: 24px;
              }
            }
          `}
        </style>
      </div>
    );
  }, [DelectBookingSchema, category, getDeleteOptions, onSubmit, t]);

  const contactTippyContent = useMemo(() => {
    return (
      <div
        className="contact-tippy-content"
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Typography variant="h9" component="div">
          {t('contact_staff')}
        </Typography>

        <div className="contact-tippy-content__wrapper">
          <div className="contact-tippy-content__item">
            <SharePhone
              className="contact-tippy-content__icon"
              width={16}
              height={12}
              fill="var(--color-greyText)"
            />
            <Typography variant="body3">{`${property?.contactAreaCode} ${property?.contactPhoneNumber}`}</Typography>
          </div>
          <div className="contact-tippy-content__item">
            <ShareEmail
              className="contact-tippy-content__icon"
              width={16}
              height={12}
              fill="var(--color-greyText)"
            />
            <Typography variant="body3">{property?.contactEmailAddress}</Typography>
          </div>
        </div>

        <style jsx>
          {`
            .contact-tippy-content {
              width: 357px;
              padding: 16px;
              border-radius: 14px;
              background: var(--color-white);
              box-shadow: 0 2px 8px 0 rgba(76, 76, 76, 0.16);
              &__wrapper {
                border-radius: 4px;
                background-color: var(--color-background);
                margin-top: 12px;
                padding: 16px 16px 8px;
              }
              &__item {
                display: flex;
                align-items: center;
                margin-bottom: 8px;
              }
            }
            :global(.contact-tippy-content__icon) {
              margin-right: 12px;
            }
          `}
        </style>
      </div>
    );
  }, [property, t]);

  const bookingExpired = useMemo(() => {
    return status === BookingStatusOption.EXPIRED;
  }, [status]);

  const bookingExpireBalance = useMemo(() => {
    if (Number(balanceInfo?.walletBalance) > 0 && status === BookingStatusOption.EXPIRED) {
      return (
        <>
          <div className="booking-expire-balance">
            <IconPayment fill="var(--color-yellow_6)" className="booking-expire-balance__icon" />
            <Typography variant="body3">
              {t(
                'amounts_paid_transferred_into_this_booking_has_been_kept_in_your_balance_for_you'
              )}
            </Typography>
          </div>
          <Typography textDecoration="underline" cursor="pointer" variant="body3" component="p">
            {t('modal.booking_details.btn')}
          </Typography>
          <style jsx>
            {`
              .booking-expire-balance {
                width: 100%;
                background-color: var(--color-yellow_1);
                padding: 12px 12px 12px 44px;
                border-radius: 12px;
                margin: 12px 0;
                position: relative;
                :global(&__icon) {
                  width: 24px;
                  height: auto;
                  position: absolute;
                  top: 14px;
                  left: 12px;
                }
              }
            `}
          </style>
        </>
      );
    }
  }, [balanceInfo, status, t]);

  return (
    <div className={cn(styles.root, props.className)} onClick={handleCardClick}>
      <div className="booking-progress-card__property-img-wraper">
        <img
          className="booking-progress-card__property-img"
          src={getPropertyImageUrl(booking?.property?.imageName)}
          alt=""
        />
        {bookingExpired && <div className="booking-progress-card__expired"></div>}
        {!bookingIsPastOrArchived && (
          <div className="booking-progress-card__status-btn">
            <Edit width={16} height={16} fill="var(--color-white)" />
            <Typography
              className="booking-progress-card__status-btn-text"
              variant="body3"
              color="var(--color-white)"
            >
              {bookingStepTitle}
            </Typography>
          </div>
        )}
        {bookingIsPastOrArchived && (
          <StatusButton
            status="black"
            name={t(`my_stay.status.${status?.toLocaleLowerCase()}`)}
            className="booking-progress-card__grey-btn"
          />
        )}
      </div>
      <div className="booking-progress-card__content">
        <Typography
          className="booking-progress-card__content-name"
          component="div"
          variant="h8"
          color={bookingExpired ? 'var(--color-gray_6)' : 'var(--color-purple_6)'}
        >
          {cardName}
        </Typography>
        {tenancyOption?.name && (
          <div className="booking-progress-card__content-date">
            {isRollingBooking && !bookingIsPastOrArchived ? (
              <Typography
                variant="body3"
                color={bookingExpired ? 'var(--color-gray_6)' : 'var(--color-purple_6)'}
              >
                {t('from')} {moment(tenancyOption?.startDate).format('ddd, ll')}
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body3"
                  color={bookingExpired ? 'var(--color-gray_6)' : 'var(--color-purple_6)'}
                >
                  {moment(tenancyOption?.startDate).format('ddd, ll')}
                </Typography>
                <IconRight
                  className="booking-progress-card__icon-right"
                  fill="var(--color-gray_6)"
                />
                <Typography
                  variant="body3"
                  color={bookingExpired ? 'var(--color-gray_6)' : 'var(--color-purple_6)'}
                >
                  {moment(tenancyOption?.endDate).format('ddd, ll')}
                </Typography>
              </>
            )}

            <Typography
              variant="body3"
              color={bookingExpired ? 'var(--color-gray_6)' : 'var(--color-purple_6)'}
            >{` (${tenancyOption?.name})`}</Typography>
          </div>
        )}
        {getPriceBillingCycle && (
          <Typography component="div" variant="body3">
            {getPriceBillingCycle}
          </Typography>
        )}
        <BookingProgressCardCountdown booking={booking} />

        {category === CategoryOption.DIRECT_LET &&
          (status === BookingStatusOption.APPLIED ||
            status === BookingStatusOption.ROOM_SELECTED) &&
          !heldToTime && (
            <div
              className="booking-progress-card__delete"
              onClick={e => {
                e.stopPropagation();
                setShowDelete(!showDelete);
              }}
            >
              <Tippy
                content={bookingDelete}
                trigger="click"
                hideOnClick={'toggle'}
                animation="scale"
                placement="bottom-start"
                className="booking-progress-card__delete-tippy"
                visible={showDelete}
                onShow={() => {
                  setShowDelete(true);
                }}
                onHide={() => {
                  setShowDelete(false);
                }}
              >
                <Typography textDecoration="underline" cursor="pointer" variant="body3">
                  {t('booking_delete')}
                </Typography>
              </Tippy>
            </div>
          )}

        {category === CategoryOption.NOMINATION && bookingIsNominationProcess && (
          <div
            className="booking-progress-card__delete"
            onClick={e => {
              e.stopPropagation();
              setShowDelete(!showDelete);
            }}
          >
            <Tippy
              content={bookingDelete}
              trigger="click"
              hideOnClick={'toggle'}
              animation="scale"
              placement="bottom-start"
              className="booking-progress-card__delete-tippy"
              visible={showDelete}
              onShow={() => {
                setShowDelete(true);
              }}
              onHide={() => {
                setShowDelete(false);
              }}
            >
              <Typography textDecoration="underline" cursor="pointer" variant="body3">
                {t('nomination.countDown.reject_offer')}
              </Typography>
            </Tippy>
          </div>
        )}
        {bookingIsPastOrArchived && status !== BookingStatusOption.EXPIRED && (
          <Typography textDecoration="underline" cursor="pointer" variant="body3">
            {t('modal.booking_details.btn')}
          </Typography>
        )}
        {bookingExpireBalance}

        {category === CategoryOption.DIRECT_LET &&
          bookingExpired &&
          Number(balanceInfo?.walletBalance) <= 0 && (
            <Typography textDecoration="underline" cursor="pointer" variant="body3">
              {t('booking_again')}
            </Typography>
          )}
        {category === CategoryOption.NOMINATION &&
          bookingExpired &&
          Number(balanceInfo?.walletBalance) <= 0 && (
            <div
              className="booking-progress-card__delete"
              onClick={e => {
                e.stopPropagation();
                setShowContact(!showContact);
              }}
            >
              <Tippy
                content={contactTippyContent}
                trigger="click"
                hideOnClick={'toggle'}
                animation="scale"
                visible={showContact}
                placement="bottom-start"
                className="booking-progress-card__delete-tippy"
              >
                <Typography textDecoration="underline" cursor="pointer" variant="body3">
                  {t('contact_staff')}
                </Typography>
              </Tippy>
            </div>
          )}
      </div>
      <style jsx>
        {`
          .booking-progress-card {
            width: 357px;
            margin-bottom: 24px;
            background: var(--color-white);
            cursor: pointer;
            &__delete {
              margin-top: 10px;
            }
            &__expired {
              width: 100%;
              height: 100%;
              position: absolute;
              top: 0;
              left: 0;
              background: rgba(255, 255, 255, 0.4);
              border-radius: 14px 14px 0 0;
            }
            &__property-img-wraper {
              width: 100%;
              height: 220px;
              position: relative;
            }
            &__property-img {
              width: 100%;
              height: 100%;
              border-radius: 14px 14px 0 0;
            }
            &__status-btn {
              border-radius: 4px;
              background: rgba(42, 42, 42, 0.7);
              position: absolute;
              bottom: 0;
              right: 0;
              padding: 2px 8px;
              display: flex;
              align-items: center;
            }
            &__content {
              padding: 12px 16px 24px;
              border: 1px solid var(--color-purple_2);
              min-height: 142px;
              border-radius: 0 0 14px 14px;
            }
            &__content-date {
              margin-top: 8px;
              margin-bottom: 8px;
              height: 20px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            :global(&__content-name) {
              height: 48px;
              word-break: break-word;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              overflow: hidden;
              -webkit-box-orient: vertical;
            }
            :global(&__icon-right) {
              width: 12px;
              margin-left: 8px;
              margin-right: 8px;
            }
            :global(&__grey-btn) {
              position: absolute;
              bottom: 12px;
              left: 12px;
            }
            :global(&__status-btn-text) {
              margin-left: 4px;
            }
          }
          :global(.booking-progress-card__delete-tippy) {
            :global(.tippy-content) {
              padding: 0;
              background: var(--color-white);
              border-radius: 4px;
            }
            :global(.tippy-arrow) {
              border-bottom-color: var(--color-white) !important;
              border-top-color: var(--color-white) !important;
            }
          }
          :global(.tippy-popper) {
            pointer-events: auto;
          }
          @media screen and (max-width: 768px) {
            .booking-progress-card {
              width: 100%;
              margin-bottom: 16px;
              padding: 16px;
              &__property-img-wraper {
                height: 211px;
              }
              &__property-img {
                border-radius: 14px;
              }
              &__content {
                padding: 12px 0 0 0;
                border: none;
                height: auto;
                min-height: auto;
                border-radius: 0;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default BookingProgressCard;
